import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "@/lib/theme";
import { applyPlatformClasses } from "@/lib/device";
import PublicHome from "@/pages/PublicHome";

// Code-split all admin pages — public users never download Recharts/admin UI bundle
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminProducts = lazy(() => import("@/pages/AdminProducts"));
const AdminProductEditor = lazy(() => import("@/pages/AdminProductEditor"));
const AdminTrips = lazy(() => import("@/pages/AdminTrips"));
const AdminRequests = lazy(() => import("@/pages/AdminRequests"));
const AdminSettings = lazy(() => import("@/pages/AdminSettings"));

const AdminLoader = () => (
  <div className="min-h-screen grid place-items-center bg-background">
    <div className="text-center">
      <div className="h-10 w-10 rounded-2xl bg-sakura-300 mx-auto opacity-70 animate-pulse" />
      <p className="mt-4 text-plum/60 dark:text-cream/70 text-[10px] uppercase tracking-[0.3em]">Memuat</p>
    </div>
  </div>
);

function RequireAuth({ children }) {
  const token = localStorage.getItem("jt_token");
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
}

export default function App() {
  useEffect(() => {
    applyPlatformClasses();
    const splash = document.getElementById("boot-splash");
    if (splash) {
      requestAnimationFrame(() => {
        splash.style.opacity = "0";
        splash.style.visibility = "hidden";
        setTimeout(() => splash.remove(), 320);
      });
    }
  }, []);
  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-background text-foreground">
        <BrowserRouter>
          <Suspense fallback={<AdminLoader />}>
            <Routes>
              <Route path="/" element={<PublicHome />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <RequireAuth>
                    <AdminLayout />
                  </RequireAuth>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="products/new" element={<AdminProductEditor />} />
                <Route path="products/:id" element={<AdminProductEditor />} />
                <Route path="trips" element={<AdminTrips />} />
                <Route path="requests" element={<AdminRequests />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster
          richColors
          position="top-center"
          theme="system"
          offset="16px"
          mobileOffset={{ top: "calc(env(safe-area-inset-top) + 12px)", left: "12px", right: "12px" }}
        />
      </div>
    </ThemeProvider>
  );
}
